import type { ConditionGroup, LexicalText, NextPageParams, PaginationMetadata } from "@/schema"
import type { Project, ProjectInput, ProjectsResponse, ProjectUpdateInput } from "@/schema/project"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export async function fetchProjects(context: ApiContext, queryParams?: string): Promise<ProjectsResponse> {
  const url = queryParams ? `projects?${queryParams}` : "projects"
  return apiContextFetch<ProjectsResponse>(context, url)
}

export async function fetchProject(context: ApiContext, id: string): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}`)
}

export async function reopenProject(context: ApiContext, id: string): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}/reopening`, { method: "POST" })
}

export async function createProject(
  context: ApiContext,
  input: ProjectInput & { form_responses?: Record<string, string | number | boolean | null> }
): Promise<Project> {
  const { form_responses, ...projectInput } = input
  return apiContextFetch<Project>(context, "projects", {
    method: "POST",
    body: JSON.stringify({ project: projectInput, form_responses }),
  })
}

export async function updateProject(
  context: ApiContext,
  id: string,
  input: ProjectUpdateInput,
  reassignTasks?: boolean
): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ project: input, reassign_tasks: reassignTasks }),
  })
}

export async function moveProjectToNextPhase(context: ApiContext, id: string): Promise<Project> {
  const data = await apiContextFetch<{ project: Project }>(context, `projects/${id}/move_to_next_phase`, {
    method: "POST",
  })
  return data.project
}

export async function checkProjectCompletion(context: ApiContext, id: string): Promise<Project> {
  const data = await apiContextFetch<{ project: Project }>(context, `projects/${id}/check_completion`, {
    method: "POST",
  })
  return data.project
}

export async function updateProjectProgress(context: ApiContext, id: string): Promise<Project> {
  const data = await apiContextFetch<{ project: Project }>(context, `projects/${id}/update_progress`, {
    method: "POST",
  })
  return data.project
}

export async function updateProjectMilestone(
  context: ApiContext,
  id: string,
  milestoneId: string,
  formResponses?: Record<string, string | number | boolean | null>
): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}/update_milestone`, {
    method: "POST",
    body: JSON.stringify({ milestone_id: milestoneId, form_responses: formResponses }),
  })
}

export interface MilestoneCompletionPayload {
  outcome_id?: string
  project_comment?: LexicalText
  mark_all_tasks_complete?: boolean
  form_responses?: Record<string, string | number | boolean | null>
}

export async function completeProjectMilestone(
  context: ApiContext,
  id: string,
  payload: MilestoneCompletionPayload = {}
): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}/milestone_completion`, {
    method: "POST",
    body: JSON.stringify({ milestone_completion: payload }),
  })
}

export interface PaginatedProjectsResponse {
  projects: Project[]
  pagination: PaginationMetadata
  milestone_counts?: Record<string, number>
}

export function fetchPaginatedProjects(
  context: ApiContext,
  search?: string,
  nextPageParams?: NextPageParams,
  viewFilterGroup?: ConditionGroup,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  includeInactive?: boolean,
  projectTemplateId?: string,
  withoutHousehold?: boolean,
  withoutProjectable?: boolean,
  projectableType?: "Household" | "Contact" | "Business",
  projectableId?: string
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (includeInactive) {
    params.append("include_inactive", "true")
  }

  if (projectTemplateId) {
    params.append("project_template_id", projectTemplateId)
  }

  if (withoutHousehold) {
    params.append("without_household", "true")
  }

  if (withoutProjectable) {
    params.append("without_projectable", "true")
  }

  if (projectableType) {
    params.append("projectable_type", projectableType)
  }

  if (projectableId) {
    params.append("projectable_id", projectableId)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  return apiContextFetch<PaginatedProjectsResponse>(context, `projects?${params.toString()}`, {
    method: "GET",
  })
}

export async function fetchHouseholdProjects(context: ApiContext, householdId: string): Promise<ProjectsResponse> {
  return apiContextFetch<ProjectsResponse>(context, `households/${householdId}/projects`)
}

export async function fetchContactProjects(context: ApiContext, contactId: string): Promise<ProjectsResponse> {
  const params = new URLSearchParams({ projectable_type: "Contact", projectable_id: contactId })
  return apiContextFetch<ProjectsResponse>(context, `projects?${params.toString()}`)
}

export async function fetchBusinessProjects(context: ApiContext, businessId: string): Promise<ProjectsResponse> {
  const params = new URLSearchParams({ projectable_type: "Business", projectable_id: businessId })
  return apiContextFetch<ProjectsResponse>(context, `projects?${params.toString()}`)
}

export async function completeProject(
  context: ApiContext,
  id: string,
  markAllTasksComplete: boolean
): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ mark_all_tasks_complete: markAllTasksComplete }),
  })
}

export async function upsertFormFieldResponses(
  context: ApiContext,
  projectId: string,
  responses: Record<string, string | number | boolean | null>
): Promise<Project> {
  return apiContextFetch<Project>(context, `projects/${projectId}/form_field_responses`, {
    method: "POST",
    body: JSON.stringify({ responses }),
  })
}

export interface HouseholdRecordOption {
  id: string
  label: string
}

export async function fetchHouseholdRecordOptions(
  context: ApiContext,
  householdId: string,
  formFieldId: string
): Promise<{ options: HouseholdRecordOption[] }> {
  return apiContextFetch<{ options: HouseholdRecordOption[] }>(
    context,
    `households/${householdId}/household_record_options/${formFieldId}`
  )
}

export async function deleteProject(context: ApiContext, id: string, destroyAllTasks: boolean): Promise<void> {
  return apiContextFetch<void>(context, `projects/${id}`, {
    method: "DELETE",
    body: JSON.stringify({ destroy_all_tasks: destroyAllTasks }),
  })
}

export interface FormFieldUploadResult {
  storage_file_id: string
  file_name: string
  file_type: string
  file_size: number
  web_url: string
}

export async function uploadFormFieldFile(
  context: ApiContext,
  householdId: string,
  projectTemplateId: string,
  file: File
): Promise<FormFieldUploadResult> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("project_template_id", projectTemplateId)

  return apiContextFetch<FormFieldUploadResult>(context, `households/${householdId}/form_field_uploads`, {
    method: "POST",
    body: formData,
    headers: { "Content-Type": undefined } as unknown as HeadersInit,
  })
}
